import { OptionalCardLink } from '@playbooks/molecules/cards/card-link';
import { Skeleton } from '@playbooks/molecules/skeletons';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@playbooks/ui/components/card';
import { CardMedia } from '@playbooks/ui/components/card';
import { Icon, resolveIcon } from '@playbooks/ui/components/icon';
import { cn } from '@playbooks/ui/lib/utils';

import type { CardContentProps, CardNavigationProps } from './types';

type GridCardProps = Pick<
	CardContentProps,
	'actions' | 'badges' | 'className' | 'icon' | 'loading' | 'media' | 'meta' | 'photo' | 'text' | 'title'
> &
	CardNavigationProps;

const GridCard = ({
	icon,
	media,
	badges,
	photo,
	title,
	text,
	meta,
	href,
	target,
	linkProps,
	loading,
	className = '',
	actions,
}: GridCardProps) => {
	// Theme
	const rootClassName = cn(
		'relative h-full gap-0 py-0 hover:border-border-strong hover:bg-surface-card-hover overflow-hidden',
		className,
	);

	// Render
	if (loading) return <Skeleton layout='grid' className={className} />;

	return (
		<Card className={rootClassName}>
			<div className='flex-end absolute top-2 right-2 gap-2 z-2'>
				{badges}
				{actions}
			</div>
			<OptionalCardLink
				variant='ghost'
				aria-label={String(title)}
				href={href}
				target={target}
				{...linkProps}
				className='group h-auto w-full rounded-none border-0 p-0'>
				{media || (
					<CardHeader className='aspect-[16/9] p-0 flex-middle overflow-hidden relative w-full bg-surface-hover'>
						{photo ? (
							<img src={photo} alt={String(title)} className='h-full w-full object-cover' />
						) : (
							<CardMedia className='h-full w-full text-foreground-body'>
								<Icon {...resolveIcon(icon, 'fad')} aria-hidden />
							</CardMedia>
						)}
					</CardHeader>
				)}
			</OptionalCardLink>
			<CardContent className='flex-column grow gap-1 border-t p-3'>
				{title && (
					<CardTitle>
						<OptionalCardLink
							variant='ghost'
							href={href}
							target={target}
							{...linkProps}
							className='h-auto p-0 hover:underline'>
							{title}
						</OptionalCardLink>
					</CardTitle>
				)}
				{text && <CardDescription className='line-clamp-1 text-xs'>{text}</CardDescription>}
			</CardContent>
			{meta && (
				<CardFooter className='flex-between gap-3 border-t-0 bg-transparent px-3 pt-0 pb-3'>
					{meta && <div className='ml-auto min-w-0 grow'>{meta}</div>}
				</CardFooter>
			)}
		</Card>
	);
};

export { GridCard };
export type { GridCardProps };
